import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';

interface DocInfo {
  id: string;
  title: string;
}

const EducationPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [docs, setDocs] = useState<DocInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeDoc, setActiveDoc] = useState<string>('');

  useEffect(() => {
    if (!token) {
      setError('유효하지 않은 링크입니다.');
      setLoading(false);
      return;
    }
    fetch(`/api/education-viewer/${token}`)
      .then((res) => {
        if (!res.ok) throw new Error('만료');
        return res.json();
      })
      .then((data) => {
        setDocs(data.documents || []);
        if (data.documents?.length > 0) setActiveDoc(data.documents[0].id);
        setLoading(false);
      })
      .catch(() => {
        setError('열람 기간이 만료되었거나 유효하지 않은 링크입니다.');
        setLoading(false);
      });
  }, [token]);

  // 복사/캡처 방지
  useEffect(() => {
    const preventContext = (e: MouseEvent) => e.preventDefault();
    const preventKeys = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && ['s', 'p', 'c', 'a'].includes(e.key.toLowerCase())) e.preventDefault();
      if (e.key === 'PrintScreen') e.preventDefault();
    };
    const preventDrag = (e: DragEvent) => e.preventDefault();

    document.addEventListener('contextmenu', preventContext);
    document.addEventListener('keydown', preventKeys);
    document.addEventListener('dragstart', preventDrag);
    return () => {
      document.removeEventListener('contextmenu', preventContext);
      document.removeEventListener('keydown', preventKeys);
      document.removeEventListener('dragstart', preventDrag);
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-400 text-lg font-semibold mb-2">{error}</div>
          <p className="text-slate-500 text-sm">관리자에게 문의해주세요.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 select-none" style={{ WebkitUserSelect: 'none', userSelect: 'none' }}>
      {/* 헤더 */}
      <div className="bg-slate-950 border-b border-slate-800 sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/grk-logo.jpg" alt="GRK" className="h-6" />
            <span className="text-white font-semibold text-sm">교육 자료</span>
            <span className="text-slate-500 text-xs">열람 전용</span>
          </div>
          <span className="text-slate-500 text-xs">GRK Partners</span>
        </div>
      </div>

      {/* 탭 */}
      <div className="bg-slate-950/50 border-b border-slate-800">
        <div className="max-w-5xl mx-auto px-4 flex gap-1">
          {docs.map((doc) => (
            <button
              key={doc.id}
              onClick={() => setActiveDoc(doc.id)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeDoc === doc.id
                  ? 'text-indigo-400 border-indigo-400'
                  : 'text-slate-400 border-transparent hover:text-slate-300'
              }`}
            >
              {doc.title}
            </button>
          ))}
        </div>
      </div>

      {/* PDF 뷰어 */}
      {activeDoc && <PdfViewer token={token!} docId={activeDoc} />}

      {/* 푸터 */}
      <div className="text-center py-6">
        <p className="text-slate-600 text-xs">
          주식회사 지알케이파트너스 · 서울시 용산구 이태원로 200, 511호
        </p>
      </div>

      {/* 인쇄 차단 */}
      <style>{`@media print { body { display: none !important; } }`}</style>
    </div>
  );
};

// PDF 렌더링 컴포넌트
const PdfViewer: React.FC<{ token: string; docId: string }> = ({ token, docId }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [pageCount, setPageCount] = useState(0);
  const [pdfLoading, setPdfLoading] = useState(true);
  const [pdfError, setPdfError] = useState(false);

  const renderIdRef = useRef(0);

  const renderPdf = useCallback(async () => {
    if (!containerRef.current) return;
    containerRef.current.innerHTML = '';
    setPdfLoading(true);
    setPdfError(false);

    const currentRenderId = ++renderIdRef.current;

    try {
      const pdfjsLib = (window as any).pdfjsLib;
      if (!pdfjsLib) throw new Error('pdf.js not loaded');

      const pdf = await pdfjsLib.getDocument(`/api/education-viewer/${token}/pdf/${docId}`).promise;
      if (currentRenderId !== renderIdRef.current) return;

      setPageCount(pdf.numPages);

      // 먼저 모든 캔버스를 순서대로 배치
      const canvases: HTMLCanvasElement[] = [];
      const dpr = window.devicePixelRatio || 1;
      const displayWidth = Math.min(window.innerWidth, 900) - 40;

      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const baseScale = displayWidth / page.getViewport({ scale: 1 }).width;
        const renderScale = baseScale * dpr;
        const viewport = page.getViewport({ scale: renderScale });
        const canvas = document.createElement('canvas');
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        canvas.style.width = `${viewport.width / dpr}px`;
        canvas.style.height = `${viewport.height / dpr}px`;
        canvas.style.maxWidth = '100%';
        canvas.style.boxShadow = '0 4px 20px rgba(0,0,0,0.4)';
        canvas.style.background = '#f1f5f9';
        canvas.style.borderRadius = '4px';
        containerRef.current?.appendChild(canvas);
        canvases.push(canvas);
      }

      // 그 다음 병렬 렌더링
      const renderPromises = canvases.map(async (canvas, idx) => {
        if (currentRenderId !== renderIdRef.current) return;
        const page = await pdf.getPage(idx + 1);
        const baseScale = displayWidth / page.getViewport({ scale: 1 }).width;
        const viewport = page.getViewport({ scale: baseScale * dpr });
        await page.render({ canvasContext: canvas.getContext('2d')!, viewport }).promise;
      });
      await Promise.all(renderPromises);
    } catch {
      if (currentRenderId === renderIdRef.current) setPdfError(true);
    } finally {
      if (currentRenderId === renderIdRef.current) setPdfLoading(false);
    }
  }, [token, docId]);

  useEffect(() => {
    renderPdf();
  }, [renderPdf]);

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      {pdfLoading && (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-400" />
        </div>
      )}
      {pdfError && (
        <div className="text-center py-20">
          <p className="text-red-400">PDF를 불러올 수 없습니다.</p>
        </div>
      )}
      {!pdfLoading && !pdfError && pageCount > 0 && (
        <div className="text-center mb-4">
          <span className="text-slate-500 text-xs">총 {pageCount}페이지</span>
        </div>
      )}
      <div ref={containerRef} className="flex flex-col items-center gap-3" />
    </div>
  );
};

export default EducationPage;
